import { type ApolloClient, useApolloClient } from '@apollo/client'
import type { ViewQueryField, ViewQueryList, ViewQueryNode } from '@salescore/core'
import { message } from 'antd'
import { Array, pipe, Result } from 'effect'
import { t } from 'i18next'

import { useUpsertViewRecordsEntry } from '../../../hooks/useUpsertViewRecordsEntry'
import type { NewRecordChange } from '../../../rsheet/action/addNewRecord/addNewRecords'
import { searchRelation } from '../../../rsheet/recoil/mutations/searchRelation'
import { computeChildInsertInnerRowIndex } from '../../../rsheet/util/computeInsertPosition'
import type { RecordChange } from '../../../rsheet/util/updateViewRecords'
import { getLabeledFieldName } from '../../../state/util'
import { useRecordsImpl } from '../../records'
import type { SalescoreObjectAbility, ViewAbility } from '../../view/atoms'
import { useCanBeCreatedWithoutAbility } from '../../view/hooks/canBeCreatedWithoutAbility'
import { getDefaultAttributes } from '../selectors/querySelector'
import type { RelationChange } from './updateRecordNodeRelations'

interface CreationFieldValue {
  field: ViewQueryField
  value: string | number | boolean
}

export const buildCreationRecordChanges = (
  defaultValues: CreationFieldValue[],
  additionalFieldValues: CreationFieldValue[],
  position: { rowIndex: number; insertInnerRowIndex: number },
): RecordChange[] =>
  [...defaultValues, ...additionalFieldValues].map(({ field, value }) => ({
    rowIndex: position.rowIndex,
    innerRowIndex: position.insertInnerRowIndex,
    field,
    nodePath: field.nodePath,
    isNestedColumn: false,
    value,
    label: undefined,
  }))

/**
 * 参照先レコードの表示名を変更内容の label に入れる
 * - 表示名が数値でもセルに出るよう、文字列にして入れる。変更履歴の valueLabel は文字列しか受け付けないため
 * @param relationChange - 参照先レコードの取得結果。参照項目でない、または参照先が見つからないときは undefined
 */
export const assignReferencedRecordLabel = (
  recordChange: RecordChange,
  relationChange: RelationChange | undefined,
): RecordChange => {
  const label = relationChange?.recordNode.attributes[getLabeledFieldName(recordChange.field.name)]
  if (typeof label !== 'string' && typeof label !== 'number') {
    return recordChange
  }
  return { ...recordChange, label: String(label) }
}

/**
 * 行追加で入るデフォルト値ごとに参照先レコードを取得し、変更内容に参照先の表示名を載せる
 * - シートのセルは行の `<項目名>_label` だけを見て表示名を出すため、ID だけだと「該当なし id: …」と表示される
 * - 新規行は attributes が空なので、参照先から表示名を写す処理 (updateRecordNodeRelations) では表示名が入らない。変更内容に載せて ID と一緒に書く
 */
export const searchDefaultValueRelations = async ({
  recordChanges,
  client,
  query,
}: {
  recordChanges: RecordChange[]
  client: ApolloClient<unknown>
  query: ViewQueryList | undefined
}): Promise<{ recordChanges: RecordChange[]; relationChanges: RelationChange[] }> => {
  const searchedChanges = await Promise.all(
    recordChanges.map(async (recordChange) => {
      const relationChange = await searchRelation({ recordChange, client, query })
      return { recordChange: assignReferencedRecordLabel(recordChange, relationChange), relationChange }
    }),
  )
  return {
    recordChanges: searchedChanges.map(({ recordChange }) => recordChange),
    relationChanges: Array.flatMapNullishOr(searchedChanges, ({ relationChange }) => relationChange),
  }
}

//
// 指定したrowIndex,innerRowIndexの行の下に新規行を追加するhook
// 0行目に挿入したい場合は-1を用いる
//
export const useAddEmptyRecordMutation = (
  query: ViewQueryList | undefined,
  ability: ViewAbility & SalescoreObjectAbility,
) => {
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const client = useApolloClient()
  const canBeCreatedWithoutAbility = useCanBeCreatedWithoutAbility()
  const records = useRecordsImpl()

  const addEmptyRecordsMutation = async ({
    rowIndex,
    innerRowIndex,
    node,
    num,
    additionalFieldValues,
  }: {
    rowIndex: number
    innerRowIndex: number
    node: ViewQueryNode
    num: number
    // 作成と同時に値を設定したいフィールド。デフォルト値と同じ単一 upsert に載せる (作成後の別 upsert は古い records を見て対象を発見できないため)
    additionalFieldValues: CreationFieldValue[]
  }): Promise<void> => {
    const baseInsertInnerRowIndexResult =
      node.path.length === 1
        ? Result.succeed(innerRowIndex)
        : pipe(records[rowIndex], (rootRecordNode) =>
            rootRecordNode === undefined
              ? Result.fail({ message: 'ルートレコードが存在しません' as const })
              : computeChildInsertInnerRowIndex(rootRecordNode, innerRowIndex, node),
          )

    if (Result.isFailure(baseInsertInnerRowIndexResult)) {
      if (baseInsertInnerRowIndexResult.failure !== undefined) {
        void message.warning(t(baseInsertInnerRowIndexResult.failure.message))
      }
      return
    }

    const baseInsertInnerRowIndex = baseInsertInnerRowIndexResult.success
    const changeGroups = Array.makeBy(num, (index) => {
      const height = 1
      const insertInnerRowIndex = baseInsertInnerRowIndex + index * height
      const newRecordChange: NewRecordChange = {
        rowIndex,
        innerRowIndex: insertInnerRowIndex,
        // 親特定は挿入位置 (insertInnerRowIndex) ではなく選択セルの生 innerRowIndex を使う。num 件追加でも全件同じ親。
        parentSelectionInnerRowIndex: innerRowIndex,
        height,
        node,
      }
      const { defaultValues } = getDefaultAttributes(node, query)
      const recordChanges = buildCreationRecordChanges(defaultValues, additionalFieldValues, {
        rowIndex,
        insertInnerRowIndex,
      })
      return { newRecordChange, recordChanges }
    })
    const { recordChanges, relationChanges } = await searchDefaultValueRelations({
      recordChanges: changeGroups.flatMap((changeGroup) => changeGroup.recordChanges),
      client,
      query,
    })
    upsertViewRecordsEntry({
      newRecordChanges: changeGroups.map(({ newRecordChange }) => newRecordChange),
      recordChanges,
      relationChanges,
    })
  }

  async function addRootRecords({ rowIndex, num }: { rowIndex: number; num: number }): Promise<number | undefined> {
    if ((!ability.canCreateNewRecord || !ability.canSaveRecord) && !ability.canCreateSalescoreObjectRecord) {
      void message.warning(t(`権限がないため、新規レコード作成はできません`))
      return
    }
    if (query === undefined) {
      // query が undefined なのは kpi/kpiPivot view 経由で本 hook が mount された場合のみ。
      // kpi/kpiPivot の column 型では本分岐 (ルート行追加) に到達しないため silent return で安全。
      return
    }
    const newRowIndex = rowIndex + 1
    await addEmptyRecordsMutation({
      rowIndex: newRowIndex,
      innerRowIndex: 0,
      node: query.tree,
      num,
      additionalFieldValues: [],
    })
    return newRowIndex
  }

  async function addChildRecords({
    rowIndex,
    innerRowIndex,
    node,
    num,
    additionalFieldValues,
  }: {
    rowIndex: number
    innerRowIndex: number
    node: ViewQueryNode
    num: number
    additionalFieldValues: CreationFieldValue[]
  }): Promise<void> {
    // 新規レコード作成権限があるかをチェック
    // SheetCustomModel＝カスタム列に対する新規作成は許す（ユーザーからの見た目は新規作成ではなく単なる編集なので）
    if (
      (!ability.canCreateNewRecord || !ability.canSaveRecord) &&
      !ability.canCreateSalescoreObjectRecord &&
      !canBeCreatedWithoutAbility(node)
    ) {
      void message.warning(t(`権限がないため、新規レコード作成はできません`))
      return
    }
    await addEmptyRecordsMutation({ rowIndex, innerRowIndex, node, num, additionalFieldValues })
  }

  return {
    addRootRecord: async ({ rowIndex }: { rowIndex: number }): Promise<number | undefined> =>
      await addRootRecords({ rowIndex, num: 1 }),
    addRootRecords,
    // 子レコードを1件作成する。`additionalFieldValues` で作成と同時にフィールド値を設定できる (値不要なら []、真偽値の空セルでキーボード操作したときの作成 + トグル等で使う)
    addChildRecord: async ({
      rowIndex,
      innerRowIndex,
      node,
      additionalFieldValues,
    }: {
      rowIndex: number
      innerRowIndex: number
      node: ViewQueryNode
      additionalFieldValues: CreationFieldValue[]
    }): Promise<void> => {
      await addChildRecords({ rowIndex, innerRowIndex, node, num: 1, additionalFieldValues })
    },
    addChildRecords,
  }
}
